import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2, Calendar, Euro, Repeat } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkSubscription = async () => {
      try {
        // Check payment status first
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          setSubscriptionDetails(data);
        } else {
          setTimeout(checkSubscription, 2000);
        }
      } catch (error) {
        console.error('Subscription check error:', error);
        setStatus('error');
      }
    };

    checkSubscription();
  }, [sessionId]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center px-4">
        <Card className="bg-light/5 border-gold/20 max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <Loader2 className="w-16 h-16 text-gold animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-playfair text-white mb-2">
              Activation de votre abonnement...
            </h2>
            <p className="text-gray-400">
              Veuillez patienter quelques instants
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center px-4">
        <Card className="bg-light/5 border-red-500/20 max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">❌</span>
            </div>
            <h2 className="text-2xl font-playfair text-white mb-2">
              Erreur de vérification
            </h2>
            <p className="text-gray-400 mb-6">
              Impossible de vérifier le statut de l'abonnement
            </p>
            <Button
              onClick={() => navigate('/services')}
              className="bg-gold hover:bg-gold-light text-dark"
            >
              Retour aux services
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center px-4">
      <Card className="bg-light/5 border-gold/20 max-w-2xl w-full">
        <CardHeader className="text-center border-b border-gold/20">
          <div className="w-20 h-20 bg-neon/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-neon" />
          </div>
          <CardTitle className="text-4xl font-playfair text-white">
            Abonnement Activé ! 🎉
          </CardTitle>
          <p className="text-gray-400 mt-2">
            Merci pour votre confiance
          </p>
        </CardHeader>
        
        <CardContent className="pt-8">
          <div className="bg-gradient-to-r from-gold/20 to-transparent rounded-sm p-6 border border-gold/20 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Repeat className="w-6 h-6 text-gold" />
              <h3 className="text-xl font-semibold text-white">Paiement Récurrent Activé</h3>
            </div>
            <div className="space-y-3 text-gray-300">
              <div className="flex justify-between">
                <span>Référence :</span>
                <span className="text-gold font-mono text-sm">{sessionId}</span>
              </div>
              {subscriptionDetails?.amount && (
                <div className="flex justify-between">
                  <span>Montant mensuel :</span>
                  <span className="text-white font-semibold flex items-center gap-1">
                    {(subscriptionDetails.amount / 100).toFixed(2)}
                    <Euro className="w-4 h-4" />
                    <span className="text-sm text-gray-400">/ mois</span>
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Statut :</span>
                <span className="text-neon font-semibold">Actif ✓</span>
              </div>
            </div>
          </div>

          <div className="bg-gold/10 border border-gold/30 rounded-sm p-4 mb-6">
            <h4 className="text-gold font-semibold mb-2 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Facturation Automatique
            </h4>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>✓ Paiement automatique chaque mois</li>
              <li>✓ Email de confirmation avant chaque prélèvement</li>
              <li>✓ Annulation possible à tout moment</li>
              <li>✓ Accès immédiat à tous les avantages</li>
            </ul>
          </div>

          <div className="bg-white/5 border border-gold/10 rounded-sm p-4 mb-6">
            <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
              <span>📧</span>
              Confirmation par email
            </h4>
            <p className="text-sm text-gray-300">
              Un email de confirmation a été envoyé avec tous les détails de votre abonnement.
              Notre équipe vous contactera sous 24h pour commencer votre suivi personnalisé.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => navigate('/')}
              className="w-full bg-gold hover:bg-gold-light text-dark"
            >
              <span>Retour à l'accueil</span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/services')}
              variant="outline"
              className="w-full border-gold/30 text-gold hover:bg-gold/10"
            >
              Voir nos autres services
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
